import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { listingAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const PHOTO_CATEGORIES = [
  { value: 'EXTERIOR',        label: 'Exterior',            key: 'photo.cat_exterior' },
  { value: 'LIVING_ROOM',     label: 'Living Room',         key: 'photo.cat_living_room' },
  { value: 'KITCHEN',         label: 'Kitchen',             key: 'photo.cat_kitchen' },
  { value: 'BEDROOM',         label: 'Bedroom',             key: 'photo.cat_bedroom' },
  { value: 'BATHROOM',        label: 'Bathroom',            key: 'photo.cat_bathroom' },
  { value: 'DINING_AREA',     label: 'Dining Area',         key: 'photo.cat_dining_area' },
  { value: 'BALCONY',         label: 'Balcony / Terrace',   key: 'photo.cat_balcony' },
  { value: 'GARDEN',          label: 'Garden / Outdoor',    key: 'photo.cat_garden' },
  { value: 'WINTER_GARDEN',   label: 'Winter Garden',       key: 'photo.cat_winter_garden' },
  { value: 'BASEMENT',        label: 'Basement / Keller',   key: 'photo.cat_basement' },
  { value: 'OFFICE',          label: 'Office / Study',      key: 'photo.cat_office' },
  { value: 'GARAGE_PARKING',  label: 'Garage / Parking',    key: 'photo.cat_garage_parking' },
  { value: 'POOL',            label: 'Pool',                key: 'photo.cat_pool' },
  { value: 'SHED',            label: 'Shed / Garden House', key: 'photo.cat_shed' },
  { value: 'UTILITY_LAUNDRY', label: 'Utility / Laundry',   key: 'photo.cat_utility_laundry' },
  { value: 'GENERAL',         label: 'Other / General',     key: 'photo.cat_general' },
];

// Maps frontend category values to backend-accepted room_type strings.
// VALID_ROOM_TYPES on backend: LIVING_ROOM, KITCHEN, BATHROOM, BEDROOM,
// FRONT_OF_HOUSE, DINING_ROOM, OFFICE, BALCONY, OUTDOOR, UTILITY, OTHER
const ROOM_TYPE_MAP = {
  BEDROOM:         'BEDROOM',
  BATHROOM:        'BATHROOM',
  KITCHEN:         'KITCHEN',
  LIVING_ROOM:     'LIVING_ROOM',
  DINING_AREA:     'DINING_ROOM',
  BALCONY:         'BALCONY',
  GARDEN:          'OUTDOOR',
  WINTER_GARDEN:   'OUTDOOR',
  EXTERIOR:        'FRONT_OF_HOUSE',
  GARAGE_PARKING:  'OUTDOOR',
  POOL:            'OUTDOOR',
  SHED:            'OUTDOOR',
  BASEMENT:        'OTHER',
  OFFICE:          'OFFICE',
  UTILITY_LAUNDRY: 'UTILITY',
  GENERAL:         'OTHER',
};

// Categories where an area/room number helps owners organise multiple spaces.
// Do not re-add Room Type as a user-facing field; it is derived from category.
const REPEATABLE_CATEGORIES = new Set([
  'BEDROOM', 'BATHROOM', 'BALCONY', 'GARDEN', 'OFFICE', 'GARAGE_PARKING',
]);

const AREA_LABELS = {
  BEDROOM:        'Bedroom #',
  BATHROOM:       'Bathroom #',
  BALCONY:        'Balcony/Terrace #',
  GARDEN:         'Garden Area #',
  OFFICE:         'Office #',
  GARAGE_PARKING: 'Garage/Parking Area #',
};

const AREA_LABEL_KEYS = {
  BEDROOM:        'photo.area_bedroom',
  BATHROOM:       'photo.area_bathroom',
  BALCONY:        'photo.area_balcony',
  GARDEN:         'photo.area_garden',
  OFFICE:         'photo.area_office',
  GARAGE_PARKING: 'photo.area_garage',
};

function deriveRoomType(category) {
  return ROOM_TYPE_MAP[category] || 'OTHER';
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILES = 20;
const MAX_BYTES = 10 * 1024 * 1024;
const SUPPORTED_FORMAT_COPY = 'Use JPG, PNG, or WEBP under 10 MB. HEIC/iPhone Live Photo formats are not supported yet.';

function validateFile(file) {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return `${file.name}: unsupported type. ${SUPPORTED_FORMAT_COPY}`;
  }
  if (file.size > MAX_BYTES) {
    return `${file.name}: exceeds 10 MB limit.`;
  }
  return null;
}

export default function PhotoUploader({ listingId, photosRemaining, bedrooms = 1, onSuccess }) {
  const { t } = useLanguage();
  const [category, setCategory] = useState('LIVING_ROOM');
  const [areaIndex, setAreaIndex] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    setSuccessMsg('');
    setErrorMsg('');
    const errors = [];
    const valid = [];

    const combined = [...selectedFiles, ...Array.from(incoming)];
    const slots = photosRemaining ?? MAX_FILES;

    if (combined.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files per upload.`);
    }
    if (combined.length > slots) {
      errors.push(
        `Only ${slots} photo slot(s) remaining on your plan. Select fewer files or upgrade.`
      );
    }

    Array.from(incoming).forEach((file) => {
      const err = validateFile(file);
      if (err) {
        errors.push(err);
      } else {
        valid.push(file);
      }
    });

    setFileErrors(errors);

    if (valid.length === 0) return;

    const newPreviews = valid.map((f) => ({
      name: f.name,
      objectUrl: URL.createObjectURL(f),
    }));

    setSelectedFiles((prev) => [...prev, ...valid]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  }, [selectedFiles, photosRemaining]);

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index].objectUrl);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setFileErrors([]);
    setSuccessMsg('');
    setErrorMsg('');
  };

  const clearAll = () => {
    previews.forEach((p) => URL.revokeObjectURL(p.objectUrl));
    setSelectedFiles([]);
    setPreviews([]);
    setFileErrors([]);
    setSuccessMsg('');
    setErrorMsg('');
    setProgress(0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !listingId) return;

    const slots = photosRemaining ?? MAX_FILES;
    if (selectedFiles.length > slots) {
      setErrorMsg(`Only ${slots} photo slot(s) remaining. Remove some files first.`);
      return;
    }

    setUploading(true);
    setProgress(0);
    setErrorMsg('');
    setSuccessMsg('');

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));
    formData.append('category', category);
    formData.append('room_type', deriveRoomType(category));
    // Backend only supports bedroom_index for BEDROOM; area index for other
    // repeatable categories (BATHROOM, BALCONY, etc.) is frontend-only until
    // backend photo metadata is extended (tracked as backend debt).
    if (category === 'BEDROOM') {
      formData.append('bedroom_index', String(areaIndex));
    }

    try {
      const res = await listingAPI.uploadPhotos(listingId, formData, (pct) =>
        setProgress(pct)
      );
      const data = res.data;
      setSuccessMsg(
        `${data.uploaded_count} photo${data.uploaded_count !== 1 ? 's' : ''} uploaded.${
          data.warnings?.length ? ` Warnings: ${data.warnings.join(' ')}` : ''
        }`
      );
      clearAll();
      if (onSuccess) onSuccess(data.photos, data.completion_percent, data.status);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.detail || 'Upload failed. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const slots = photosRemaining ?? MAX_FILES;
  const overSlots = selectedFiles.length > slots;
  const canUpload = selectedFiles.length > 0 && !uploading && !overSlots && fileErrors.length === 0;

  return (
    <div className="space-y-4">
      {/* Metadata selectors — Do not re-add Room Type as a user-facing field; it is derived from category. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-600 text-xs mb-0.5 block">{t('owner.photo_category', 'Photo category')}</Label>
          <p className="text-xs text-slate-400 mb-1">{t('owner.photo_category_hint', 'Choose what this photo shows.')}</p>
          <Select value={category} onValueChange={(v) => { setCategory(v); setAreaIndex(1); }}>
            <SelectTrigger className="bg-white border-slate-300 text-slate-800">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300">
              {PHOTO_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} className="text-slate-700">
                  {t(c.key, c.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {REPEATABLE_CATEGORIES.has(category) && (
          <div>
            <Label className="text-slate-600 text-xs mb-0.5 block">{t(AREA_LABEL_KEYS[category] || 'photo.area_area', AREA_LABELS[category] || 'Area #')}</Label>
            <p className="text-xs text-slate-400 mb-1">&nbsp;</p>
            <Select
              value={String(areaIndex)}
              onValueChange={(v) => setAreaIndex(Number(v))}
            >
              <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                {Array.from({ length: 6 }, (_, i) => i + 1).map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-slate-700">
                    {t(AREA_LABEL_KEYS[category] || 'photo.area_area', AREA_LABELS[category] || 'Area #').replace('#', String(n))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Drag-and-drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-signal-blue bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'
        }`}
      >
        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-slate-600">
          {t('photo_uploader.drag_drop_hint', 'Drag & drop photos here, or click to select')}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {t('photo_uploader.formats_hint', 'JPG, PNG, WEBP · Max 10 MB each')} · Up to {Math.min(MAX_FILES, slots)} {slots !== 1 ? t('photo_uploader.files_plural', 'files') : t('photo_uploader.file_singular', 'file')}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {t('photo_uploader.heic_warning', 'HEIC/iPhone Live Photo formats are not supported yet. Export as JPG or PNG first.')}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Validation errors */}
      {fileErrors.length > 0 && (
        <div className="space-y-1">
          {fileErrors.map((e, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{e}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selected file previews */}
      {previews.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {selectedFiles.length} {selectedFiles.length !== 1 ? t('photo_uploader.files_plural', 'files') : t('photo_uploader.file_singular', 'file')} {t('photo_uploader.selected', 'selected')}
              {overSlots && (
                <span className="ml-2 text-red-600 text-xs font-normal">
                  ({selectedFiles.length - slots} {t('photo_uploader.over_plan_limit', 'over plan limit')})
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-500 hover:text-red-500 transition-colors"
            >
              {t('photo_uploader.clear_all', 'Clear all')}
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {previews.map((p, i) => (
              <div key={i} className="relative group rounded overflow-hidden border border-slate-200">
                <img
                  src={p.objectUrl}
                  alt={p.name}
                  className="w-full aspect-square object-cover"
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Remove ${p.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
                <p className="text-xs text-slate-500 truncate px-1 pb-1 bg-white">
                  {p.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('photo_uploader.uploading', 'Uploading…')} {progress}%
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-signal-blue transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Success */}
      {successMsg && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {successMsg}
        </div>
      )}

      {/* Error */}
      {errorMsg && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {errorMsg}
        </div>
      )}

      {/* Upload button */}
      <Button
        onClick={handleUpload}
        disabled={!canUpload}
        className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('photo_uploader.uploading', 'Uploading…')}
          </>
        ) : (
          <>
            <ImageIcon className="w-4 h-4 mr-2" />
            {selectedFiles.length > 0
              ? `${t('photo_uploader.upload', 'Upload')} ${selectedFiles.length} ${selectedFiles.length !== 1 ? t('photo_uploader.photos_plural', 'Photos') : t('photo_uploader.photo_singular', 'Photo')}`
              : t('common.upload_photos', 'Upload Photos')}
          </>
        )}
      </Button>
    </div>
  );
}
